import React, { useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useQuery } from 'convex/react'
import { Navigate, useLocation } from 'react-router-dom'
import { useUI } from '../../contexts/UIContext'
import { api } from '../../convex_generated/api'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireRole?: 'reader' | 'writer' | 'artist' | 'admin'
  fallbackPath?: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireRole,
  fallbackPath = '/',
}) => {
  const { isLoaded, isSignedIn } = useUser()
  const { openAuthModal } = useUI()
  const location = useLocation()
  const convexUser = useQuery(api.auth.getCurrentUser)

  const needsAuth = isLoaded && !isSignedIn

  // Open auth modal when redirecting unauthenticated users back to the homepage
  useEffect(() => {
    if (needsAuth && fallbackPath === '/') {
      openAuthModal()
    }
  }, [needsAuth, fallbackPath, openAuthModal])

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (!isSignedIn) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />
  }

  // A role-gated page waits for the profile, so its contents never flash up for
  // someone who turns out not to have the role. (The server enforces access
  // regardless — this is only about what gets rendered.)
  if (requireRole && convexUser === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (requireRole && (!convexUser || (convexUser.role !== requireRole && convexUser.role !== 'admin'))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-md w-full mx-auto">
          <div className="card p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 18.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="mt-4 text-lg font-medium text-neutral-900 dark:text-neutral-100">Access Denied</h3>
            <p className="mt-2 text-neutral-600 dark:text-neutral-400">
              You need {requireRole} privileges to access this page.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default ProtectedRoute
